// ── Workspace Dashboard ──

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast, closeModal, esc, projectSwatchColor, schemeAccentColor, schemeSurfaceStyle } from '../ui.js';
import { escapeHtml, renderAvatarHtml } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext, refreshProjectList } from '../layout.js';
import { openCreateProjectModal } from '../components/create-project-modal.js';
import { navigate, getQueryParams, setQueryParams } from '../router.js';
import { loadStatuses, loadWorkspaceStatuses, statusNames, statusLabel, isClosedStatus, closedNames } from '../lib/statuses.js';
import { mountWorkspaceTaskList } from './workspace-tasks.js';

// ── State ──

let state = {};
function resetState() {
  state = {
    user: null,
    tasks: [],
    projects: [],
    allTasks: [],
    statuses: [], // cross-project {name,color} union (C1182) — see loadWorkspaceStatuses()
    projectStatuses: {}, // per-project status registries, keyed String(project.id) (C1304) — see loadMetricsAndMembers()
    projectMembers: {},
    metricsLoaded: false,
    allTasksPromise: null,
    allTasksCleanup: null,
  };
}
resetState();

// ── Helpers ──

// Badge/card accent color for a project (C1266) — its color_scheme swatch, or a per-id
// hash color while color_scheme is still 'default'. See projectSwatchColor() in ui.js.
function projectColor(projectId) {
  // Workspace task payloads and /projects can disagree on numeric-vs-string ids. Resolve
  // by value so a task always receives its owning project's scheme instead of the fallback.
  const project = state.projects.find(p => String(p.id) === String(projectId));
  return projectSwatchColor(project && project.color_scheme, projectId);
}

// Owning project's color_scheme, or null (no match yet / still 'default') — same
// id-normalizing lookup projectColor() uses.
function projectScheme(projectId) {
  const project = state.projects.find(p => String(p.id) === String(projectId));
  return project ? project.color_scheme : null;
}

// Inline-style fragment (C1472) carrying --proj-bg/--proj-text/--proj-muted/--proj-track
// for a project-owned surface (card, chip) — '' while color_scheme is still 'default', so
// callers append it directly after --proj-color without an extra branch. See
// schemeSurfaceStyle() in ui.js.
function projectSurface(projectId) {
  return schemeSurfaceStyle(projectScheme(projectId));
}

// Status chip color for one task/segment (C1304): resolves the color TOKEN from that
// project's OWN status registry once loaded (falls back to the cross-project union
// before it lands), then resolves the token to a literal hex inside that SAME project's
// palette via schemeAccentColor() — so a chip always paints the owning project's scheme,
// never the viewer's workspace theme.
function statusColor(projectId, statusName) {
  const ownRows = state.projectStatuses[String(projectId)];
  const row = (ownRows || state.statuses).find(s => s.name === statusName);
  return schemeAccentColor(projectScheme(projectId), row && row.color);
}

// Dynamic per-name tally (C1182) — a project's statuses can be arbitrary now, so this
// can no longer assume a fixed 4-name shape. Excludes the canceled-role name — resolved
// from that project's OWN registry once loaded (C1304; a project's own registry is
// authoritative about which of ITS statuses means canceled), the cross-project union's
// aggregated is_workflow_canceled flag (C1187) otherwise. Completed tasks are kept so the
// tally still reflects them.
function computeProjectMetrics(projectId) {
  const ownRows = state.projectStatuses[String(projectId)];
  const canceledNames = new Set((ownRows || state.statuses || []).filter(s => s.is_workflow_canceled).map(s => s.name));
  const m = { total: 0 };
  for (const t of state.allTasks) {
    if (t.project_id !== projectId || canceledNames.has(t.status)) continue;
    m[t.status] = (m[t.status] || 0) + 1;
    m.total++;
  }
  return m;
}

function formatGreetingDate() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

// ── Render: Metrics Bar ──

function renderMetricsBar(metrics, projectId) {
  if (!metrics.total) return '<div class="dash-metrics-bar"></div>';
  const segments = Object.keys(metrics)
    .filter(s => s !== 'total' && metrics[s] > 0)
    .map(s => {
      const pct = (metrics[s] / metrics.total * 100).toFixed(1);
      return `<div style="width:${pct}%;background:${statusColor(projectId, s)};"></div>`;
    })
    .join('');
  return `<div class="dash-metrics-bar">${segments}</div>`;
}

// ── Render: Member Stack ──

function renderMemberStack(members) {
  if (!members || !members.length) return '';
  const show = members.slice(0, 3);
  const extra = members.length - 3;
  let html = show.map(m => renderAvatarHtml(m, 28)).join('');
  if (extra > 0) {
    html += `<div class="dash-member-more">+${extra}</div>`;
  }
  return html;
}

// ── Render: My Task Row ──

function renderMyTaskRow(task) {
  const color = projectColor(task.project_id);
  // Own project's status names once loaded — a project with custom names otherwise gets
  // offered the union's names (C1304); union stays the pre-load fallback.
  const ownRows = state.projectStatuses[String(task.project_id)];
  const options = statusNames(ownRows || state.statuses).map(s =>
    `<option value="${escapeHtml(s)}"${s === task.status ? ' selected' : ''}>${escapeHtml(statusLabel(s))}</option>`
  ).join('');
  return `<div class="dash-task-row" data-task-key="${escapeHtml(task.task_key)}" data-project-id="${task.project_id}">
    <span class="ws-project-badge" style="--proj-color:${color}${projectSurface(task.project_id)}">${escapeHtml(task.project_name || '')}</span>
    <a class="dash-task-key task-key-link" href="#/projects/${task.project_id}/tasks/${encodeURIComponent(task.task_key)}" draggable="false" title="Open task page">${escapeHtml(task.task_key)}</a>
    <span class="dash-task-title">${escapeHtml(task.title)}</span>
    <select class="form-select task-status-select dash-status-select" data-task-key="${escapeHtml(task.task_key)}" data-project-id="${task.project_id}" style="--status-color:${statusColor(task.project_id, task.status)}">${options}</select>
  </div>`;
}

// ── Render: My Tasks Section ──

function renderMyTasks() {
  const el = document.getElementById('dash-my-tasks');
  if (!el) return;
  if (!state.tasks.length) {
    el.innerHTML = '<div class="dash-empty">No active tasks assigned to you</div>';
    return;
  }
  el.innerHTML = state.tasks.map(renderMyTaskRow).join('');
}

// ── Render: Project Card ──

function renderProjectCard(project) {
  const desc = project.description
    ? escapeHtml(project.description.length > 80 ? project.description.slice(0, 80) + '...' : project.description)
    : '';
  const metricsHtml = state.metricsLoaded
    ? renderMetricsBar(computeProjectMetrics(project.id), project.id)
    : '<div class="dash-metrics-bar"></div>';
  const membersHtml = state.projectMembers[project.id]
    ? renderMemberStack(state.projectMembers[project.id])
    : '';

  const color = projectColor(project.id);
  return `<div class="card card-clickable dash-project-card" data-id="${project.id}" style="--proj-color:${color}${projectSurface(project.id)}">
    <div class="dash-project-title-row">
      <span class="dash-project-swatch"></span>
      <div class="card-title">${escapeHtml(project.name)}</div>
    </div>
    ${desc ? `<div class="card-meta dash-project-desc">${desc}</div>` : ''}
    <div class="dash-metrics-wrap" id="dash-metrics-${project.id}">${metricsHtml}</div>
    <div class="dash-project-footer">
      <span class="card-meta">${project.task_count || 0} tasks</span>
      <div class="dash-member-stack" id="dash-members-${project.id}">${membersHtml}</div>
    </div>
  </div>`;
}

// ── Render: Projects Grid ──

function renderProjects() {
  const el = document.getElementById('dash-project-grid');
  if (!el) return;
  if (!state.projects.length) {
    el.innerHTML = `<div class="card card-clickable dash-new-project" id="dash-new-project">
      <span class="dash-new-project-icon">+</span><span>New Project</span>
    </div>`;
    return;
  }
  el.innerHTML = state.projects.map(renderProjectCard).join('')
    + `<div class="card card-clickable dash-new-project" id="dash-new-project">
        <span class="dash-new-project-icon">+</span><span>New Project</span>
      </div>`;
}

function getDashboardTab() {
  return getQueryParams().get('tab') === 'all' ? 'all' : 'my';
}

// Dashboard content is split into panels, but My Tasks retains all content previously
// rendered on the dashboard. All Tasks is mounted lazily from workspace-tasks.js.
export function renderDashboard() {
  const activeTab = getDashboardTab();
  return `
    <div class="dash-tabs-row">
      <div class="dash-tabs" role="tablist" aria-label="Dashboard views">
        <button class="dash-tab${activeTab === 'my' ? ' active' : ''}" id="dash-tab-my" type="button" role="tab" aria-controls="dash-panel-my" aria-selected="${activeTab === 'my'}" tabindex="${activeTab === 'my' ? '0' : '-1'}" data-tab="my">My Tasks</button>
        <button class="dash-tab${activeTab === 'all' ? ' active' : ''}" id="dash-tab-all" type="button" role="tab" aria-controls="dash-panel-all" aria-selected="${activeTab === 'all'}" tabindex="${activeTab === 'all' ? '0' : '-1'}" data-tab="all">All Tasks</button>
      </div>
    </div>
    <section class="dash-tab-panel" id="dash-panel-my" role="tabpanel" aria-labelledby="dash-tab-my"${activeTab === 'my' ? '' : ' hidden'}>
      <div class="dash-header">
        <div class="dash-welcome">
          ${renderAvatarHtml(state.user, 48)}
          <div>
            <h2>Welcome, ${esc(state.user.name || 'there')}</h2>
            <p class="text-muted">${formatGreetingDate()}</p>
          </div>
        </div>
      </div>
      <section class="dash-section">
        <h3 class="dash-section-title">My Tasks</h3>
        <div class="dash-my-tasks" id="dash-my-tasks">
          <div class="empty"><span class="spinner"></span></div>
        </div>
      </section>
      <section class="dash-section">
        <h3 class="dash-section-title">Projects</h3>
        <div class="grid dash-project-grid" id="dash-project-grid">
          <div class="empty"><span class="spinner"></span></div>
        </div>
      </section>
    </section>
    <section class="dash-tab-panel" id="dash-panel-all" role="tabpanel" aria-labelledby="dash-tab-all"${activeTab === 'all' ? '' : ' hidden'}>
      <div id="dash-all-tasks"><div class="empty"><span class="spinner"></span></div></div>
    </section>`;
}

function updateDashboardTabQuery(tab) {
  const params = getQueryParams();
  if (tab === 'all') params.set('tab', 'all');
  else params.delete('tab');
  setQueryParams(params);
}

// Switch dashboard panels and mount All Tasks once. Keeping the promise in state makes
// repeated clicks and keyboard activation share the same request and list instance.
export async function activateDashboardTab(tab, { updateUrl = true } = {}) {
  const activeTab = tab === 'all' ? 'all' : 'my';
  const content = getContentEl();
  if (!content) return;

  content.querySelectorAll('.dash-tab').forEach(button => {
    const isActive = button.dataset.tab === activeTab;
    button.classList.toggle('active', isActive);
    button.setAttribute('aria-selected', String(isActive));
    button.tabIndex = isActive ? 0 : -1;
  });
  content.querySelectorAll('.dash-tab-panel').forEach(panel => {
    panel.hidden = panel.id === 'dash-panel-' + activeTab ? false : true;
  });

  if (updateUrl) updateDashboardTabQuery(activeTab);

  if (activeTab !== 'all' || state.allTasksPromise) return state.allTasksPromise;

  const mount = content.querySelector('#dash-all-tasks');
  if (!mount) return;

  const promise = mountWorkspaceTaskList(mount, {
    includeHeader: true,
    projects: state.projects,
    statuses: state.statuses,
  }).catch(err => {
    showToast(err.message || 'Failed to load all tasks', 'error');
    return null;
  });
  state.allTasksPromise = promise;
  promise.then(cleanup => {
    if (state.allTasksPromise === promise) state.allTasksCleanup = cleanup;
  });
  return promise;
}

// ── Background: Metrics + Members ──

async function loadMetricsAndMembers() {
  // Fetch all workspace tasks for metrics — unfiltered (C1182): computeProjectMetrics()
  // now tallies whatever status names actually occur, not a fixed 4-name shape, so a
  // server-side status= filter would silently under-count any project's custom names.
  try {
    const res = await api.get('/workspace/tasks?limit=200');
    state.allTasks = res.tasks || [];
    state.metricsLoaded = true;
    updateAllMetricsBars();
  } catch (_) { /* skip */ }

  // Fetch members + each project's own status registry in parallel (C1304). loadStatuses()
  // is memoised per project id and never rejects (falls back to the legacy 5) — same call
  // updateTaskStatus() already makes, so this shares its cache.
  const memberFetches = state.projects.map(p =>
    api.get('/projects/' + p.id + '/members')
      .then(res => {
        state.projectMembers[p.id] = res.members || [];
        updateMemberStack(p.id);
      })
      .catch(() => {})
  );
  const statusFetches = state.projects.map(p =>
    loadStatuses(p.id).then(rows => { state.projectStatuses[String(p.id)] = rows; })
  );
  await Promise.allSettled([...memberFetches, ...statusFetches]);

  // Statuses land after the union-based first paint above — repaint metrics segments and
  // My Tasks rows/options with each project's own palette now that it's known (C1304).
  updateAllMetricsBars();
  renderMyTasks();
}

function updateAllMetricsBars() {
  for (const p of state.projects) {
    const wrap = document.getElementById('dash-metrics-' + p.id);
    if (wrap) wrap.innerHTML = renderMetricsBar(computeProjectMetrics(p.id), p.id);
  }
}

function updateMemberStack(projectId) {
  const el = document.getElementById('dash-members-' + projectId);
  if (el) el.innerHTML = renderMemberStack(state.projectMembers[projectId]);
}

// ── Status Update ──

async function updateTaskStatus(projectId, taskKey, newStatus) {
  try {
    await api.patch('/projects/' + projectId + '/tasks/' + taskKey, { status: newStatus });
    // This task's own project's role registry (not the cross-project union) resolves
    // whether newStatus actually means "done" — complete OR canceled role (C1187).
    const projectStatuses = await loadStatuses(projectId);
    const isDone = isClosedStatus(projectStatuses, newStatus);
    if (isDone) {
      state.tasks = state.tasks.filter(t => t.task_key !== taskKey);
      renderMyTasks();
    } else {
      const task = state.tasks.find(t => t.task_key === taskKey);
      if (task) task.status = newStatus;
    }
    showToast('Status updated');
  } catch (err) {
    showToast(err.message || 'Failed to update', 'error');
    renderMyTasks();
  }
}

// ── Events ──

function bindDashboardTabs() {
  const content = getContentEl();
  if (!content) return;

  const tabs = [...content.querySelectorAll('.dash-tab')];
  tabs.forEach((button, index) => {
    button.addEventListener('click', () => {
      void activateDashboardTab(button.dataset.tab);
    });
    button.addEventListener('keydown', (e) => {
      let nextIndex = index;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
      if (e.key === 'Home') nextIndex = 0;
      if (e.key === 'End') nextIndex = tabs.length - 1;
      if (nextIndex === index) return;
      e.preventDefault();
      tabs[nextIndex].focus();
      void activateDashboardTab(tabs[nextIndex].dataset.tab);
    });
  });
}

function bindEvents() {
  const content = getContentEl();
  if (!content) return;

  content.addEventListener('click', (e) => {
    // New project card
    if (e.target.closest('#dash-new-project')) {
      openCreateProjectModal();
      return;
    }
    // Project card
    const card = e.target.closest('.dash-project-card[data-id]');
    if (card) {
      navigate('/projects/' + card.dataset.id + '/board');
      return;
    }
    // Task row (skip if clicking select)
    const row = e.target.closest('.dash-task-row');
    if (row && !e.target.closest('select, a')) {
      navigate('/projects/' + row.dataset.projectId + '/board');
      return;
    }
  });

  content.addEventListener('change', (e) => {
    const sel = e.target.closest('.dash-status-select');
    if (sel) {
      updateTaskStatus(Number(sel.dataset.projectId), sel.dataset.taskKey, sel.value);
    }
  });
}

// ── Page Entry ──

export async function dashboardPage() {
  if (!(await checkAuth())) return;
  resetState();
  state.user = getUser();

  initLayout(state.user);
  updateLayoutContext({ projectId: null, projectName: null, activeView: 'dashboard', viewLabel: 'Dashboard' });

  const content = getContentEl();
  content.innerHTML = renderDashboard();

  // Parallel fetch: cross-project status union + projects list; "my tasks" waits on the
  // status union (C1182) so its own status= filter can name whatever's actually active
  // across every project instead of the old fixed 3-name literal list.
  const statusesPromise = loadWorkspaceStatuses();
  const projectsPromise = api.get('/projects');
  try {
    state.statuses = await statusesPromise;
    const closed = new Set(closedNames(state.statuses));
    const activeNames = statusNames(state.statuses).filter(n => !closed.has(n));
    const statusParam = activeNames.length ? `&status=${activeNames.map(encodeURIComponent).join(',')}` : '';
    const [tasksRes, projectsRes] = await Promise.all([
      api.get(`/workspace/tasks?assignee=me${statusParam}&limit=20`),
      projectsPromise,
    ]);
    state.tasks = tasksRes.tasks || [];
    state.projects = projectsRes.projects || [];
  } catch (err) {
    showToast(err.message || 'Failed to load dashboard', 'error');
  }

  renderMyTasks();
  renderProjects();
  bindEvents();
  bindDashboardTabs();
  void activateDashboardTab(getDashboardTab(), { updateUrl: false });

  // Background enrichment
  loadMetricsAndMembers();
  refreshProjectList();

  return () => {
    if (state.allTasksCleanup) state.allTasksCleanup();
    closeModal();
    resetState();
  };
}
